// =============================================
// Nature of Document → account head + memo  (acctg_doc_nature, migration 142)
// =============================================
// A nature is what a document is FOR, in the user's words: "Meal", "Fuel", "Office
// supplies". It sits on top of the fixed document kinds (Expense Receipt, Purchase
// Invoice…): each nature names its base kind and its expense bucket. The user can pick
// one or type a new one in Filing; related words ("food", "dining", "buffet") find it.
//
// Bookkeeping then takes its account head from it, first that applies:
//   1. the vendor's document line for this nature (Masters › Vendor › Document types)
//   2. the books' account that lists this nature (acctg_coa.nature_ids, migration 143)
//   3. the account these books set up for the nature (acctg_doc_nature_account)
//   4. the vendor's default account
//   5. a guess: the books' account whose name matches the nature / expense type
// and writes the memo "Meal at India Palace, paid from Visa".
//
// Pure (no React, no I/O) so the rules can be unit-tested.

export interface DocNature {
  nature_id: number;
  account_owner_id: number | null;   // null = the system list
  nature_name: string;
  match_words: string;               // comma-separated related words
  docu_type_id: number | null;       // base kind (acctg_docu_type)
  expense_type_id: number | null;    // expense bucket (acctg_expense_type)
  sort_order: number;
  overrides_nature_id: number | null; // an account's edited copy of this standard nature
}

export const toDocNature = (r: any): DocNature => ({
  nature_id: Number(r.nature_id),
  account_owner_id: r.account_owner_id == null ? null : Number(r.account_owner_id),
  nature_name: String(r.nature_name || '').trim(),
  match_words: String(r.match_words || ''),
  docu_type_id: r.docu_type_id == null ? null : Number(r.docu_type_id),
  expense_type_id: r.expense_type_id == null ? null : Number(r.expense_type_id),
  sort_order: Number(r.sort_order) || 0,
  overrides_nature_id: r.overrides_nature_id == null ? null : Number(r.overrides_nature_id),
});

/** The natures one account can use: the system list + its own, live ones only. An own
 *  nature replaces the standard one it was edited from (`overrides_nature_id` — also when
 *  the copy is switched off, which is how a standard nature is removed from one account's
 *  list) and the standard one of the same name. Own ones first. */
export function naturesForAccount(rows: any[], accountOwnerId: number | null): DocNature[] {
  const notDeleted = rows.filter(r => Number(r.is_deleted) !== 1);
  // eslint-disable-next-line eqeqeq
  const ownAll = notDeleted.filter(r => accountOwnerId != null && r.account_owner_id != null && r.account_owner_id == accountOwnerId);
  const replaced = new Set(ownAll.filter(r => r.overrides_nature_id != null).map(r => Number(r.overrides_nature_id)));
  const own = ownAll.filter(r => Number(r.is_active) !== 0).map(toDocNature);
  const ownNames = new Set(own.map(n => n.nature_name.toLowerCase()));
  const system = notDeleted.filter(r => r.account_owner_id == null && Number(r.is_active) !== 0).map(toDocNature)
    .filter(n => !replaced.has(n.nature_id) && !ownNames.has(n.nature_name.toLowerCase()));
  const byOrder = (a: DocNature, b: DocNature) => a.sort_order - b.sort_order || a.nature_name.localeCompare(b.nature_name);
  return [...own.sort(byOrder), ...system.sort(byOrder)];
}

/** id → the nature it shows as now: each listed nature by its own id, and a standard
 *  nature an account edited by the id of its copy, so documents, vendor lines and
 *  account set-ups saved against the standard one still find it. */
export function natureLookup(natures: DocNature[], rows: any[] = []): Map<number, DocNature> {
  const m = new Map<number, DocNature>();
  // a copy taken back ("Back to standard"): what was saved with it finds the standard one
  const listed = new Map(natures.map(n => [n.nature_id, n] as const));
  rows.forEach(r => {
    const std = r.overrides_nature_id != null ? listed.get(Number(r.overrides_nature_id)) : undefined;
    if (std && Number(r.is_deleted) === 1) m.set(Number(r.nature_id), std);
  });
  natures.forEach(n => { if (n.overrides_nature_id != null) m.set(n.overrides_nature_id, n); });
  natures.forEach(n => m.set(n.nature_id, n));
  return m;
}

/** The ids a row may carry for this nature: its own, and the standard one it replaces. */
export const natureIds = (n: DocNature): number[] =>
  n.overrides_nature_id != null ? [n.nature_id, n.overrides_nature_id] : [n.nature_id];

const norm = (s: string) => ` ${String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
export const natureWords = (n: DocNature): string[] =>
  [n.nature_name, ...n.match_words.split(',')].map(w => w.trim()).filter(Boolean);

/** An existing nature whose name or related word IS what the user typed ("lunch" → Meal). */
export function findNatureByName(natures: DocNature[], typed: string): DocNature | null {
  const q = norm(typed).trim();
  if (!q) return null;
  return natures.find(n => norm(n.nature_name).trim() === q)
    || natures.find(n => natureWords(n).some(w => norm(w).trim() === q))
    || null;
}

/** The nature a document's own words point to (a receipt from "India Palace & Buffet"
 *  → Meal); the longest phrase found wins. Else the first nature of the suggested
 *  expense type. Null when neither says anything. */
export function suggestNature(natures: DocNature[], text: string, expenseTypeId?: number | null): DocNature | null {
  const hay = norm(text);
  let best: DocNature | null = null;
  let bestLen = 0;
  if (hay.trim()) {
    for (const n of natures) {
      for (const w of natureWords(n)) {
        const phrase = norm(w);
        if (phrase.trim().length >= 3 && hay.includes(phrase) && phrase.length > bestLen) { best = n; bestLen = phrase.length; }
      }
    }
  }
  if (best) return best;
  if (expenseTypeId != null) return natures.find(n => n.expense_type_id === Number(expenseTypeId)) || null;
  return null;
}

// ---- account head ----

export interface CoaLite {
  coa_id: number;
  account_number?: string | null;
  account_name: string;
  finsta_cate_id?: number | null;
  alias_names?: string | null;
}

/** The words of a name worth comparing: no "and", no punctuation, a plural "s" dropped. */
function keyWords(s: string): string[] {
  return norm(s).trim().split(' ')
    .filter(w => w.length >= 3 && !['and', 'the', 'for', 'from', 'other'].includes(w))
    .map(w => (w.length > 4 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w));
}

/** The books' account that best fits the given names (nature, expense type): most
 *  shared words, an account of the expense type's statement category first. Null when
 *  no account shares a word. */
export function guessAccount(accounts: CoaLite[], names: string[], preferCateId?: number | null): CoaLite | null {
  const want = new Set(names.flatMap(keyWords));
  if (!want.size) return null;
  let best: CoaLite | null = null;
  let bestScore = 0;
  for (const a of accounts) {
    const have = new Set(keyWords(`${a.account_name} ${a.alias_names || ''}`));
    let score = 0;
    want.forEach(w => { if (have.has(w)) score += 1; });
    if (!score) continue;
    // eslint-disable-next-line eqeqeq
    if (preferCateId != null && a.finsta_cate_id == preferCateId) score += 0.5;
    if (score > bestScore) { best = a; bestScore = score; }
  }
  return best;
}

export type AccountHeadSource = 'vendor' | 'account' | 'books' | 'vendor-default' | 'guess';
export interface AccountHead { coaId: number; source: AccountHeadSource }

export const ACCOUNT_HEAD_SOURCE_LABEL: Record<AccountHeadSource, string> = {
  vendor: 'set up for this vendor',
  account: 'set up on the account',
  books: 'set up for this nature',
  'vendor-default': "vendor's default account",
  guess: 'suggested by name',
};

/** The account head for a document. Every coa id must be one of `accounts` (the books'
 *  detail accounts) — an id from other books is skipped. */
export function resolveAccountHead(opts: {
  accounts: CoaLite[];
  vendorLineCoaId?: number | null;
  /** an account of these books whose Nature of Document lists this nature */
  accountNatureCoaId?: number | null;
  booksCoaId?: number | null;
  vendorDefaultCoaId?: number | null;
  names: string[];
  preferCateId?: number | null;
}): AccountHead | null {
  const has = (id?: number | null) => id != null && opts.accounts.some(a => a.coa_id === Number(id));
  if (has(opts.vendorLineCoaId)) return { coaId: Number(opts.vendorLineCoaId), source: 'vendor' };
  if (has(opts.accountNatureCoaId)) return { coaId: Number(opts.accountNatureCoaId), source: 'account' };
  if (has(opts.booksCoaId)) return { coaId: Number(opts.booksCoaId), source: 'books' };
  if (has(opts.vendorDefaultCoaId)) return { coaId: Number(opts.vendorDefaultCoaId), source: 'vendor-default' };
  const g = guessAccount(opts.accounts, opts.names, opts.preferCateId);
  return g ? { coaId: g.coa_id, source: 'guess' } : null;
}

// ---- memo ----

/** "Meal at India Palace, paid from Visa" — a missing part is left out. */
export function buildMemo(nature: string, partyName: string, paidFrom: string): string {
  const n = String(nature || '').trim();
  const p = String(partyName || '').trim();
  const m = String(paidFrom || '').trim();
  let out = n && p ? `${n} at ${p}` : n || p;
  if (m) out = out ? `${out}, paid from ${m}` : `Paid from ${m}`;
  return out;
}
